/**
 * Portal Configuration Utilities
 * Provides data-driven portal configuration without hard-coding
 */

export interface PortalConfig {
  defaultPath: string;
  label: string;
  description: string;
  aliases?: string[];
  accessTokens?: string[];
  allowedUserTypes?: string[];
  routeTarget?: string;
  priority?: number;
  branding?: PortalBranding;
}

export interface PortalPolicy {
  globalAccessTokens?: string[];
  legacyRedirects?: Record<string, string>;
}

export interface PortalBranding {
  appName?: string;
  logoSrc?: string;
  logoAlt?: string;
  shellTitle?: string;
}

export interface ResolvedPortalBranding {
  portalKey: string;
  portalLabel: string;
  appName: string;
  logoSrc: string;
  logoAlt: string;
  description: string;
  shellTitle: string;
}

export interface PortalConfigMap {
  portals: Record<string, PortalConfig>;
  policy?: PortalPolicy;
}

// Minimal fallback to avoid hardcoded portal details in strict mode
const MINIMAL_FALLBACK: PortalConfigMap = { portals: {} };

let cachedConfig: PortalConfigMap | null = null;
let pendingConfig: Promise<PortalConfigMap> | null = null;

const PORTAL_CONFIG_TIMEOUT_MS = 2500;

const fetchPortalConfig = async (path: string): Promise<PortalConfigMap | null> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PORTAL_CONFIG_TIMEOUT_MS);
  try {
    const response = await fetch(resolveFetchUrl(path), { signal: controller.signal });
    if (!response.ok) return null;
    const config = await response.json() as PortalConfigMap;
    return config && typeof config === 'object' && config.portals && typeof config.portals === 'object'
      ? config
      : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

const normalizePortalKey = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

const resolveFetchUrl = (path: string): string => {
  try {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    if (origin) return new URL(path, origin).toString();
  } catch {
    // fall through to the original path
  }
  return path;
};

const toTitleCase = (value: string): string =>
  String(value || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const mergePortalConfigMaps = (base: PortalConfigMap, override?: PortalConfigMap | null): PortalConfigMap => {
  const merged: PortalConfigMap = {
    portals: { ...(base?.portals || {}) },
    policy: {
      ...(base?.policy || {}),
      ...(override?.policy || {}),
      legacyRedirects: {
        ...(base?.policy?.legacyRedirects || {}),
        ...(override?.policy?.legacyRedirects || {}),
      },
    },
  };

  for (const [portalKey, overrideConfig] of Object.entries(override?.portals || {})) {
    const baseConfig: Partial<PortalConfig> = merged.portals[portalKey] || {};
    merged.portals[portalKey] = {
      ...baseConfig,
      ...overrideConfig,
      accessTokens: Array.isArray(overrideConfig?.accessTokens) && overrideConfig.accessTokens.length > 0
        ? Array.from(new Set([...(baseConfig.accessTokens || []), ...overrideConfig.accessTokens]))
        : baseConfig.accessTokens,
      aliases: Array.isArray(overrideConfig?.aliases) && overrideConfig.aliases.length > 0
        ? Array.from(new Set([...(baseConfig.aliases || []), ...overrideConfig.aliases]))
        : baseConfig.aliases,
      branding: {
        ...(baseConfig.branding || {}),
        ...(overrideConfig?.branding || {}),
      },
    };
  }

  return merged;
};

export function resolvePortalConfigEntry(portal: string, config: PortalConfigMap): { key: string; config: PortalConfig } | null {
  const normalized = normalizePortalKey(portal);
  if (!normalized) return null;

  const direct = config.portals[normalized];
  if (direct) return { key: normalized, config: direct };

  for (const [key, portalConfig] of Object.entries(config.portals || {})) {
    const aliases = Array.isArray(portalConfig.aliases) ? portalConfig.aliases : [];
    if (aliases.map(normalizePortalKey).includes(normalized)) {
      return { key, config: portalConfig };
    }
  }

  return null;
}

export function resolvePortalBranding(portal: string, config: PortalConfigMap): ResolvedPortalBranding {
  const resolved = resolvePortalConfigEntry(portal, config);
  const portalKey = resolved?.key || normalizePortalKey(portal);
  const portalConfig = resolved?.config;
  const portalLabel = portalConfig?.label || toTitleCase(portalKey);
  const appName = portalConfig?.branding?.appName || 'Sankhla Consultants';
  const logoSrc = String(portalConfig?.branding?.logoSrc || '/sankhla-favicon.png').trim();
  const logoAlt = String(portalConfig?.branding?.logoAlt || appName || portalLabel || 'Sankhla Consultants').trim();
  const description = String(portalConfig?.description || '').trim();
  const shellTitle = String(portalConfig?.branding?.shellTitle || portalConfig?.branding?.appName || appName || portalLabel || 'Sankhla Consultants').trim();

  return {
    portalKey,
    portalLabel,
    appName,
    logoSrc,
    logoAlt,
    description,
    shellTitle,
  };
}

/**
 * Load portal configuration from external JSON file
 * Falls back to embedded config if loading fails
 */
export async function loadPortalConfig(): Promise<PortalConfigMap> {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (pendingConfig) {
    return pendingConfig;
  }

  pendingConfig = (async () => {
    const isTestEnv = (() => {
      try { return typeof process !== 'undefined' && !!process.env && process.env.NODE_ENV === 'test'; } catch { return false; }
    })();

    // The versioned static file is the startup source of truth. It is served by
    // the same host as the app and must not make first paint depend on the API or
    // database being healthy.
    const staticConfig = await fetchPortalConfig('/config/portal-config.json');
    if (staticConfig) {
      cachedConfig = staticConfig;
      return staticConfig;
    }

    if (!isTestEnv) {
      console.warn('Failed to load portal-config.json; falling back to backend portal config');
    }

    // The public endpoint is a bounded fallback for unusual deployments where
    // the static asset is unavailable. A slow API can never hold the UI open.
    const backendConfig = await fetchPortalConfig('/api/config/portal');
    if (backendConfig) {
      cachedConfig = mergePortalConfigMaps(MINIMAL_FALLBACK, backendConfig);
      return cachedConfig;
    }

    cachedConfig = MINIMAL_FALLBACK;
    return MINIMAL_FALLBACK;
  })().catch((error) => {
    if (typeof process === 'undefined' || !process.env || process.env.NODE_ENV !== 'test') {
      console.warn('Error loading portal config, using minimal fallback:', error);
    }
    cachedConfig = MINIMAL_FALLBACK;
    return MINIMAL_FALLBACK;
  }).finally(() => {
    pendingConfig = null;
  });

  return pendingConfig;
}

/**
 * Get the default path for a portal
 */
export function getPortalDefaultPath(portal: string, config: PortalConfigMap): string {
  return config.portals[portal]?.defaultPath || '/';
}

/**
 * Get all available portals from config
 */
export function getAvailablePortalsFromConfig(config: PortalConfigMap): string[] {
  return Object.keys(config.portals);
}

/**
 * Synchronous access to cached config (must call loadPortalConfig first)
 */
export function getCachedPortalConfig(): PortalConfigMap {
  return cachedConfig || MINIMAL_FALLBACK;
}

/**
 * Clear cached portal config for tests and forced reloads.
 */
export function clearPortalConfigCache(): void {
  cachedConfig = null;
  pendingConfig = null;
}
